import type { AlignmentStatus, Metro } from '../types/metro';
import { getStatus } from './utils';

/**
 * Hex-string linear interpolation between two colors. Used by
 * `ratioColor()` to build a continuous red→yellow→green scale.
 * Operates in straight RGB; sufficient for the brand-token gradient
 * we ship without pulling a color library.
 */
function lerpHex(a: string, b: string, t: number): string {
  const clamp = Math.max(0, Math.min(1, t));
  const parse = (h: string) => [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
  const [ar, ag, ab] = parse(a);
  const [br, bg, bb] = parse(b);
  const mix = (x: number, y: number) =>
    Math.round(x + (y - x) * clamp)
      .toString(16)
      .padStart(2, '0');
  return `#${mix(ar!, br!)}${mix(ag!, bg!)}${mix(ab!, bb!)}`;
}

/**
 * Continuous credentials-to-jobs ratio → hex color.
 *
 * Anchors:
 *   - 0.00 → `#C6354C` (rose / deep shortage)
 *   - 1.00 → `#F9A21C` (gold / parity inflection)
 *   - 1.80 → `#27AE60` (green / strong surplus)
 *
 * The 0.0–1.0 leg interpolates rose → gold (shortage of varying
 * severity). The 1.0–1.8 leg interpolates gold → green (surplus of
 * varying depth). Values above 1.8 clamp to the green endpoint;
 * negatives clamp to the rose endpoint. Replaces the categorical
 * shortage / balanced / surplus 3-color treatment per the
 * 2026-05-18 feedback pass — the report doesn't argue a "balanced"
 * band, so we don't render one.
 */
export function ratioColor(ratio: number): string {
  const r = !Number.isFinite(ratio) ? 0 : ratio;
  if (r <= 1) {
    return lerpHex('#C6354C', '#F9A21C', r);
  }
  return lerpHex('#F9A21C', '#27AE60', (r - 1) / 0.8);
}

/**
 * Bubble radius (meters) driven by the ratio itself rather than total
 * job count. Smaller ratios (severe shortages) produce LARGER bubbles
 * so the visual weight is on the metros most in need of intervention
 * — the project's headline story.
 *
 * Mapping: factor = 8 + 36 * (1 - clamp(ratio, 0, 1.5) / 1.5). A
 * 0.20 metro renders ~3.3× larger than a 1.50 metro. Floor of 8 (same
 * minimum as the prior jobs-based radius) so even surpluses stay
 * visible. Output multiplied by 1200 to match the legacy
 * `L.circle({ radius: factor * 1200 })` units.
 */
/**
 * Background + text color pair for the Rankings severity pill.
 *
 * Anchors specified in the 2026-05-18 feedback round:
 *   - 0.22 (San Diego-Chula Vista) → darkest red
 *   - 0.87 (Boston) → light yellow
 *
 * Between 0.22 and 0.87 we interpolate dark red → light yellow.
 * Above 1.00 we continue the gradient toward green so surplus metros
 * stay legible on the same scale. Text color flips to white on the
 * dark-red and dark-green endpoints so contrast clears WCAG AA.
 */
export function severityPillStyle(ratio: number): { bg: string; text: string } {
  const r = !Number.isFinite(ratio) ? 1 : ratio;
  let bg: string;
  if (r <= 0.22) {
    bg = '#A02437'; // darkest red — clamp anchor
  } else if (r <= 0.87) {
    const t = (r - 0.22) / (0.87 - 0.22);
    bg = lerpHex('#A02437', '#FFE57A', t);
  } else if (r <= 1.0) {
    bg = '#FFE57A'; // light yellow plateau on the upper-shortage end
  } else {
    const t = Math.min(1, (r - 1) / 0.8);
    bg = lerpHex('#FFE57A', '#27AE60', t);
  }
  // A11y (WCAG 1.4.3): pick the ink that actually clears AA on THIS bg rather
  // than guessing from the ratio. The prior ratio-threshold logic forced white
  // on the r>1.4 "surplus" band, but that band's bg is still LIGHT green
  // (e.g. #8ac76c at r≈1.4) where white is only ~2:1 — a serious contrast fail.
  // Choosing by measured contrast keeps Luis's gradient colors UNTOUCHED (the
  // color still encodes the ratio); only the number's ink adapts. Verified: the
  // better of black/white clears ≥4.5:1 at every point of the gradient.
  const text = accessibleInk(bg);
  return { bg, text };
}

/**
 * WCAG relative luminance of a `#rrggbb` hex color.
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function relativeLuminance(hex: string): number {
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two `#rrggbb` colors (1–21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Pick `#000000` or `#ffffff` — whichever has the HIGHER WCAG contrast on
 * `bg` — so text on a dynamic (data-driven) background stays legible. Used by
 * the severity pill, whose background is a continuous ratio gradient.
 */
export function accessibleInk(bg: string): '#000000' | '#ffffff' {
  return contrastRatio('#ffffff', bg) >= contrastRatio('#000000', bg) ? '#ffffff' : '#000000';
}

export function ratioRadius(ratio: number): number {
  const r = !Number.isFinite(ratio) || ratio < 0 ? 0 : Math.min(1.5, ratio);
  // 2026-07-08 (CR-13, V2 PDF feedback: "dot-size scale breadth" —
  // metros looked similarly sized). The 2026-07-02 pass widened the
  // factor spread from 8-44 to 6-60, but because the real dataset's
  // metro-level ratios cluster almost entirely inside the shortage
  // band (see DEC-056/CR-03 note: 55/55 metros are sub-1.0 today),
  // that spread only translated to a ~2.5x factor difference across
  // the observed data, not the full 10x the domain implies. Widened
  // again to 4-84 (floor lowered slightly, ceiling raised more) so
  // metros within the realistic 0.2-0.9 band read as visibly
  // different sizes, while the floor (4 -> 4800m) stays comfortably
  // above the map's minimum-visible-bubble threshold and the
  // ceiling (84 -> 100800m) stays well inside a single US-viewport
  // bubble, not overwhelming. Same linear shape end to end (keeps
  // the mental model that ratio and size trade off 1:1) -- just a
  // fatter dynamic range.
  //
  // Floor (ratio = 1.5) -> factor = 4 -> 4800 meters.
  // Ceiling (ratio = 0) -> factor = 84 -> 100800 meters.
  const factor = 4 + 80 * (1 - r / 1.5);
  return factor * 1200;
}

/**
 * Tailwind text-color class for a status, used by the metro list rows
 * and the popup sector ratio chips. Mirrors the prototype's
 * `.metro-ratio.shortage|surplus` color rules (DEC-056: no balanced band).
 *
 * LUIS-fix-1: surplus → `text-green2` (was `text-blue`) per Luis's May 6
 * directive — surplus is GREEN throughout the map per the Georgetown
 * reference report. See `_archive/code/nav.css` (`--green2: #1d8348`)
 * + Luis's updated `_inbox/luis-fixes/map.html` lines 252-256.
 */
export function statusTextColor(status: AlignmentStatus): string {
  switch (status) {
    case 'shortage':
      return 'text-rose';
    case 'surplus':
      return 'text-green2';
  }
}

/**
 * Tailwind bg-color class for a status, used by the metro list row
 * progress bar fills. Mirrors `.metro-bar-fill.shortage|surplus` (DEC-056: no balanced band).
 *
 * LUIS-fix-1: surplus → `bg-green2` (was `bg-blue`).
 */
export function statusBgColor(status: AlignmentStatus): string {
  switch (status) {
    case 'shortage':
      return 'bg-rose';
    case 'surplus':
      return 'bg-green2';
  }
}

/** Sector definitions used in the metro popup's sector breakdown. */
export interface SectorMeta {
  readonly name: string;
  readonly color: string;
}

/**
 * 5-name palette inherited from the static prototype. Used as a color
 * source for `getSectorData()` rows when the metro carries real
 * `occupationGroups` data — the real data has 22 group names; we pick
 * a color from this palette by hashing the name so colors stay stable
 * but the names come from the real data.
 *
 * Names ('Management', 'STEM', etc.) intentionally match the OLD
 * 5-group taxonomy, used now ONLY when seed-math fallback is engaged
 * (test fixtures without `occupationGroups`).
 */
export const SECTOR_META: ReadonlyArray<SectorMeta> = [
  { name: 'Management', color: '#3B82F6' },
  { name: 'STEM', color: '#60A5FA' },
  { name: 'Health', color: '#22C55E' },
  { name: 'Protective Services', color: '#F59E0B' },
  { name: 'Blue-collar', color: '#EF4444' },
] as const;

/** Default colors cycled through for the 22-group palette when the
 * group name doesn't match a SECTOR_META entry. */
const PALETTE: ReadonlyArray<string> = [
  '#3B82F6', '#60A5FA', '#22C55E', '#F59E0B', '#EF4444',
  '#A855F7', '#14B8A6', '#F97316', '#EC4899', '#84CC16',
  '#06B6D4', '#8B5CF6',
];

function colorForGroupName(name: string): string {
  const meta = SECTOR_META.find((s) => s.name === name);
  if (meta) return meta.color;
  // Stable hash → palette index, so the same group always gets the same color.
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(hash) % PALETTE.length] ?? PALETTE[0]!;
}

/** A per-sector breakdown row shown inside the metro popup. */
export interface SectorRow extends SectorMeta {
  readonly jobs: number;
  readonly ratio: number;
  readonly status: AlignmentStatus;
  /**
   * Georgetown's supplied annual credentials awarded for this group
   * (`awardsSb`). MUST be read straight through — never reconstructed
   * from `jobs * ratio` (CR-013: multiplying a rounded ratio by a large
   * openings count amplifies rounding into whole credentials, which is
   * exactly the discrepancy Emma reported). `null` only in the seed-math
   * fallback path (legacy fixtures with no real awards), rendered as an
   * em-dash by consumers.
   */
  readonly credentials: number | null;
  /**
   * Georgetown's supplied annual credential shortage for this group.
   * Read straight through — never `max(0, jobs - credentials)`, which is
   * our own definition, not Georgetown's. `null` in the seed-math
   * fallback path.
   */
  readonly shortage: number | null;
}

/**
 * Per-sector breakdown for a metro.
 *
 * Two paths:
 *   1. **Real data path (preferred)** — when `metro.occupationGroups` is
 *      present (production data from the Georgetown xlsx via PR #23
 *      backend ingest), each row maps to a `SectorRow` with the real
 *      ratio + jobs + status. Color is sourced from `SECTOR_META` if
 *      the group name matches one of the 5 legacy names, else cycled
 *      from a 12-color palette via stable hash.
 *   2. **Seed-math fallback** — when `occupationGroups` is absent
 *      (older test fixtures predating PR #23), generate a deterministic
 *      5-row breakdown from the metro's coordinates. Same code path the
 *      prototype used; preserves backward compatibility for tests that
 *      didn't migrate yet.
 *
 * Replaces the seed-math-only behavior from PR #19 and earlier.
 */
export function getSectorData(
  metro: Pick<Metro, 'lat' | 'lng' | 'ratio' | 'totalJobs' | 'occupationGroups'>,
): SectorRow[] {
  // Path 1: real data if available.
  const groups = metro.occupationGroups;
  if (groups && groups.length > 0) {
    return groups.map((g) => ({
      name: g.name,
      color: colorForGroupName(g.name),
      jobs: g.numOpeningsTotal,
      ratio: g.ratio,
      status: g.status,
      credentials: g.awardsSb,
      shortage: g.shortage,
    }));
  }

  // Path 2: seed-math fallback for legacy fixtures.
  // Seeded pseudo-random based on the metro's coordinates; matches map.html's
  // `s = (n) => { const x = Math.sin((metro.lat + metro.lng)*1000 + n)*10000; return x - Math.floor(x); }`
  const seed = (n: number): number => {
    const x = Math.sin((metro.lat + metro.lng) * 1000 + n) * 10000;
    return x - Math.floor(x);
  };

  // Base shares (sum to 1.0); jitter each by a small amount so different metros
  // get different splits but the breakdown is still deterministic per metro.
  const baseShares = [0.30, 0.20, 0.18, 0.17, 0.15] as const;
  const jittered = baseShares.map((b, i) => Math.max(0.05, b + (seed(i * 3) - 0.5) * 0.12));
  const total = jittered.reduce((a, b) => a + b, 0);

  return SECTOR_META.map((sec, i) => {
    const share = (jittered[i] ?? 0) / total;
    const jobs = Math.round(metro.totalJobs * share);
    const variance = (seed(i * 7 + 2) - 0.5) * 1.2;
    const ratio = Math.max(0.1, Math.round((metro.ratio + variance) * 100) / 100);
    return {
      ...sec,
      jobs,
      ratio,
      status: getStatus(ratio),
      // Seed-math fallback carries no real awards. Null (not a
      // jobs*ratio reconstruction) so consumers render an em-dash;
      // this path is legacy fixtures only and never reaches production.
      credentials: null,
      shortage: null,
    };
  });
}


